import React, { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import TCPACheckbox from './TCPACheckbox';
import { FinanceLinkFormData } from '../types';

interface FinanceLinkFormProps {
  onSubmit: (data: FinanceLinkFormData) => Promise<void>;
  isLoading: boolean;
  dealerName: string;
}

const FinanceLinkForm: React.FC<FinanceLinkFormProps> = ({
  onSubmit,
  isLoading,
  dealerName,
}) => {
  const [formData, setFormData] = useState<Partial<FinanceLinkFormData>>({
    firstName: '',
    lastName: '',
    phone: '',
    tcpaConsent: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.tcpaConsent) {
      newErrors.tcpaConsent = 'You must agree to receive messages';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validate()) {
      await onSubmit(formData as FinanceLinkFormData);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        overflowY: 'auto',
        flex: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
          borderRadius: '8px',
        },
      }}
    >
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Apply for Financing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We'll text you our quick online finance application — it only takes a couple minutes.
      </Typography>

      <TextField
        fullWidth
        label="First name"
        value={formData.firstName}
        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
        error={!!errors.firstName}
        helperText={errors.firstName}
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Last name"
        value={formData.lastName}
        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
        error={!!errors.lastName}
        helperText={errors.lastName}
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        type="tel"
        label="Phone number"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        error={!!errors.phone}
        helperText={errors.phone}
        disabled={isLoading}
        sx={{ mb: 3 }}
      />

      <TCPACheckbox
        checked={formData.tcpaConsent || false}
        onChange={(checked) => setFormData({ ...formData, tcpaConsent: checked })}
        dealerName={dealerName}
        error={!!errors.tcpaConsent}
        disabled={isLoading}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={isLoading}
        sx={{
          mt: 3,
          py: 1.5,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Me the Application'}
      </Button>
    </Box>
  );
};

export default FinanceLinkForm;
